import React from 'react';
import debounce from 'lodash/debounce';
import {
  Badge,
  Button,
  Box,
  Menu,
  MenuItem,
  Divider,
  Collapse,
  Typography,
  IconButton,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  useTheme,
  Tooltip,
  alpha,
  Skeleton,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Menu as MenuIcon,
  ExpandMore as ExpandMoreIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Work as WorkIcon,
  FilterList as FilterListIcon,
  AccountBalance as AccountBalanceIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  MoreHoriz as MoreHorizIcon,
  Build as BuildIcon,
} from '@mui/icons-material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getGroupedNavigationForUser, getAdminNavigationByCategory, adminCategoryLabels } from '../lib/roles';
import { usePendingFollowUpsCount } from '../hooks/usePendingFollowUps';
import { useManufacturerForClient } from '../hooks/useManufacturerForClient';
import { usePendingApprovalsCount } from '../hooks/useServiceCases';
import { canViewCompanyFollowUps } from '@trade-connector/shared';
import { supabase } from '../lib/supabase';
import { isThisBrowserPushOn } from '../lib/webPush';

import { NavDropdownConfig } from './navigation/types';
import { getIconComponent, getCategoryIcon } from './navigation/NavigationIcons';
import { MobileNavigation } from './navigation/MobileNavigation';
import { AdminNavSearch } from './navigation/AdminNavSearch';
import { AdminCommandPalette } from './navigation/AdminCommandPalette';

import { playNotificationSound } from '../utils/notificationSound';

const EMPTY_PERMISSIONS: string[] = [];

/**
 * Desktop alert for a bell notification that arrives while the tab is hidden.
 * Skipped when this browser has web push on: the service worker shows those,
 * so showing it here too would double up.
 */
async function showHiddenTabAlert(title?: string | null, message?: string | null): Promise<void> {
  if (await isThisBrowserPushOn()) return;
  try {
    new Notification(title || "New Notification", {
      body: message || "You have received a new notification.",
    });
  } catch {
    // Ignore notification construction errors on unsupported mobile browsers
  }
}

const RoleBasedNavigation: React.FC = () => {
  const location = useLocation();
  const { user, isLoading } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const isCompactNav = useMediaQuery(theme.breakpoints.down('xl'));

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [openGroup, setOpenGroup] = React.useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [expandedCategories, setExpandedCategories] = React.useState<Record<string, boolean>>({});
  const [adminQuery, setAdminQuery] = React.useState('');
  const userPermissions = React.useMemo(() => user?.permissions || [], [user?.permissions]);
  const navigate = useNavigate();

  // We must call hooks unconditionally before early returns.
  // We'll safely fallback to empty arrays/objects if user is missing.
  const permissions = user?.permissions ?? EMPTY_PERMISSIONS;
  const accountType = (user?.account_type === 'dual' ? user?.active_role : user?.account_type) ?? user?.account_type;
  const directJobsEnabled = user?.direct_jobs_enabled;

  const isManagerOrOwner = canViewCompanyFollowUps(
    null,
    user?.roles,
    user?.is_platform_admin,
  );
  const { data: followUpCount = 0 } = usePendingFollowUpsCount(user?.id, {
    companyId: user?.trade_company_id || user?.company_id,
    isManagerOrOwner,
  });
  const { manufacturer: fleetManufacturer, enabled: fleetEnabled } = useManufacturerForClient();
  const { data: fleetApprovalsCount = 0 } = usePendingApprovalsCount(
    fleetManufacturer?.id,
    fleetEnabled,
  );
  // Badge counts shown on dropdown items, keyed by the item label.
  const itemBadge = (label: string): number =>
    label === 'Follow-ups' ? followUpCount : label === 'Approvals' ? fleetApprovalsCount : 0;

  const [unreadMessagesCount, setUnreadMessagesCount] = React.useState(0);

  const debouncedFetchUnreadCount = React.useMemo(
    () =>
      debounce(async () => {
        if (!user?.id || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(user.id)) return;
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { data: rpcData, error: rpcError } = await (supabase.rpc as any)('get_user_unread_counts', {
            p_user_id: user.id,
          });

          if (!rpcError && rpcData) {
            const row = (Array.isArray(rpcData) ? rpcData[0] : rpcData) as { total_unread?: number } | null;
            if (row?.total_unread !== undefined) {
              setUnreadMessagesCount(Number(row.total_unread));
              return;
            }
          }

          const { count, error } = await supabase
            .from('messages')
            .select('id', { count: 'exact', head: true })
            .or(`read_by_user_ids.is.null,read_by_user_ids.not.cs.{${user.id}}`)
            .neq('sender_id', user.id);

          if (!error && count !== null) {
            setUnreadMessagesCount(count);
          }
        } catch {
          // Suppress error noise
        }
      }, 300),
    [user?.id]
  );

  React.useEffect(() => {
    if (!user?.id) {
      setUnreadMessagesCount(0);
      return;
    }

    debouncedFetchUnreadCount();

    // 1. Listen to real-time notification inserts/updates
    const channel = supabase
      .channel('global-unread-messages')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_id=eq.${user.id}`,
        },
        (payload) => {
          const row = (payload.new || payload.old) as {
            related_entity_type?: string | null;
            title?: string | null;
            message?: string | null;
          } | null;

          const isMessageNotification = row?.related_entity_type === 'conversation';

          if (isMessageNotification) {
            debouncedFetchUnreadCount();
          }

          if (payload.eventType === 'INSERT') {
            if (isMessageNotification) {
              playNotificationSound();
            }

            if (document.visibilityState === 'hidden' && 'Notification' in window && Notification.permission === 'granted') {
              void showHiddenTabAlert(row?.title, row?.message);
            }
          }
        }
      )
      .subscribe();

    // 2. Listen to local event when messages are read by this client (avoiding unfiltered WAL subscriptions)
    const handleLocalRead = () => {
      debouncedFetchUnreadCount();
    };
    window.addEventListener('unread-messages-changed', handleLocalRead);

    return () => {
      supabase.removeChannel(channel);
      window.removeEventListener('unread-messages-changed', handleLocalRead);
      debouncedFetchUnreadCount.cancel();
    };
  }, [user?.id, debouncedFetchUnreadCount]);

  // Extract inline fallback arrays into useMemo to maintain referential equality and prevent child re-renders
  const {
    mainItems, jobItems, leadItems, financialItems, directItems, settingsItems, adminItems
  } = React.useMemo(() => {
    if (!permissions || permissions.length === 0) {
      return { mainItems: [], jobItems: [], leadItems: [], financialItems: [], directItems: [], settingsItems: [], adminItems: [] };
    }
    const grouped = getGroupedNavigationForUser(permissions, accountType, directJobsEnabled);
    return {
      mainItems: grouped.main || [],
      jobItems: grouped.jobs || [],
      leadItems: grouped.leads || [],
      financialItems: grouped.financials || [],
      directItems: grouped.direct || [],
      settingsItems: grouped.settings || [],
      adminItems: grouped.admin || [],
    };
  }, [permissions, accountType, directJobsEnabled]);

  // For platform admins, skip company-specific dropdowns to keep the top bar compact.
  // They get Dashboard, Inbox, Jobs, Settings, and Admin.
  // Leads, Financials, and Direct are hidden as they're company-specific.
  const isPlatformAdmin = adminItems.length > 0;

  // Build dropdown configs for the navigation (only include non-empty groups)
  const dropdownConfigs = React.useMemo<NavDropdownConfig[]>(() => {
    const configs: NavDropdownConfig[] = [];

    if (jobItems.length > 0) {
      configs.push({
        key: 'jobs',
        label: 'Jobs',
        icon: <WorkIcon fontSize="small" />,
        items: jobItems,
        tooltip: 'Job Management',
      });
    }

    // Manufacturer fleet (service-enabled manufacturer client companies only)
    if (fleetEnabled) {
      configs.push({
        key: 'fleet',
        label: 'Fleet',
        icon: <BuildIcon fontSize="small" />,
        items: [
          { label: 'Registrations', path: '/dashboard/fleet' },
          { label: 'Cases', path: '/dashboard/fleet/cases' },
          { label: 'Approvals', path: '/dashboard/fleet/approvals' },
          { label: 'Catalogue', path: '/dashboard/fleet/catalogue' },
          { label: 'Network', path: '/dashboard/fleet/network' },
        ],
        tooltip: 'Manufacturer Fleet',
      });
    }

    if (!isPlatformAdmin && leadItems.length > 0) {
      configs.push({
        key: 'leads',
        label: 'Leads',
        icon: <FilterListIcon fontSize="small" />,
        items: leadItems,
        tooltip: 'Leads & Quotes',
      });
    }

    if (!isPlatformAdmin && financialItems.length > 0) {
      configs.push({
        key: 'financials',
        label: 'Financials',
        icon: <AccountBalanceIcon fontSize="small" />,
        items: financialItems,
        tooltip: 'Financial Overview',
      });
    }

    if (!isPlatformAdmin && directItems.length > 0) {
      configs.push({
        key: 'direct',
        label: 'Direct',
        icon: <PeopleIcon fontSize="small" color="secondary" />,
        items: directItems,
        tooltip: 'Direct-to-Customer',
        color: 'secondary',
      });
    }

    if (settingsItems.length > 0) {
      configs.push({
        key: 'settings',
        label: 'Company',
        icon: <BusinessIcon fontSize="small" />,
        items: settingsItems,
        tooltip: 'Company Settings',
      });
    }

    return configs;
  }, [jobItems, isPlatformAdmin, leadItems, financialItems, directItems, settingsItems, fleetEnabled]);

  // Platform admin dropdown config (handled separately for categorized rendering)
  const adminDropdownConfig: NavDropdownConfig | null = adminItems.length > 0
    ? {
        key: 'admin',
        label: 'Admin',
        icon: <AdminPanelSettingsIcon fontSize="small" />,
        items: adminItems,
        tooltip: 'Platform Administration',
        color: 'warning',
      }
    : null;

  // Split dropdowns adaptively: on compact viewports (< 1536px), show the primary dropdowns and consolidate remaining into "More".
  // On wide displays (>= 1536px), show all dropdowns directly.
  const { visibleDropdowns, moreDropdowns } = React.useMemo(() => {
    if (!isCompactNav || dropdownConfigs.length <= 2) {
      return { visibleDropdowns: dropdownConfigs, moreDropdowns: [] };
    }
    return {
      visibleDropdowns: dropdownConfigs.slice(0, 2),
      moreDropdowns: dropdownConfigs.slice(2),
    };
  }, [isCompactNav, dropdownConfigs]);

  const isMoreActive = React.useMemo(() => {
    return moreDropdowns.some((dropdown) =>
      dropdown.items.some((item) => location.pathname === item.path)
    );
  }, [moreDropdowns, location.pathname]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, group: string) => {
    setAnchorEl(event.currentTarget);
    setOpenGroup(group);
  };

  const handleMenuClose = () => {
    setAdminQuery('');
    setAnchorEl(null);
    setOpenGroup(null);
  };

  const handleMobileMenuToggle = () => {
    handleMenuClose();
    setMobileMenuOpen((v) => !v);
  };

  // Close any open desktop menu dropdown when navigating to a new path
  React.useEffect(() => {
    handleMenuClose();
  }, [location.pathname]);

  // Default expand the admin category containing the current route
  React.useEffect(() => {
    if (!user) return;
    const adminNav = getAdminNavigationByCategory(user.permissions || []);
    const activeCategory = Object.keys(adminNav).find((cat) =>
      adminNav[cat].some((item) => item.path === location.pathname)
    );
    if (activeCategory) {
      setExpandedCategories((prev) => ({
        ...prev,
        [activeCategory]: true,
      }));
    }
  }, [location.pathname, user]);

  // Show skeleton loading state while auth is initializing
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Skeleton variant="rounded" width={80} height={36} />
        <Skeleton variant="rounded" width={60} height={36} />
        <Skeleton variant="rounded" width={70} height={36} />
      </Box>
    );
  }

  if (!user) {
    return null;
  }

  // If user exists but has no permissions, show at least Dashboard link
  // instead of skeleton bars that never resolve
  if (!user?.permissions || user.permissions.length === 0) {
    console.warn('RoleBasedNavigation: No permissions found for user', user?.id);
    if (isMobile) {
      return (
        <>
          <IconButton
            aria-label="open navigation menu"
            onClick={handleMobileMenuToggle}
            edge="start"
            sx={{ color: 'rgba(238,242,232,0.9)' }}
          >
            <MenuIcon />
          </IconButton>
          <MobileNavigation
            mobileMenuOpen={mobileMenuOpen}
            setMobileMenuOpen={setMobileMenuOpen}
            mainItems={[{ path: '/dashboard', label: 'Dashboard' }]}
            dropdowns={[]}
            adminDropdown={null}
            location={location}
            followUpCount={followUpCount}
            unreadMessagesCount={unreadMessagesCount}
          />
        </>
      );
    }
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button
          component={Link}
          to="/dashboard"
          variant="text"
          sx={{ textTransform: 'none', color: 'rgba(238,242,232,0.82)' }}
        >
          Dashboard
        </Button>
      </Box>
    );
  }

  // Mobile view - show hamburger menu
  if (isMobile) {
    return (
      <>
        <IconButton
          aria-label="open navigation menu"
          onClick={handleMobileMenuToggle}
          edge="start"
          sx={{ color: 'rgba(238,242,232,0.9)' }}
        >
          <MenuIcon />
        </IconButton>
        <MobileNavigation
          mobileMenuOpen={mobileMenuOpen}
          setMobileMenuOpen={setMobileMenuOpen}
          mainItems={mainItems}
          dropdowns={dropdownConfigs}
          adminDropdown={adminDropdownConfig}
          location={location}
          followUpCount={followUpCount}
          unreadMessagesCount={unreadMessagesCount}
          fleetApprovalsCount={fleetApprovalsCount}
        />
      </>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {/* Main items (Dashboard, Inbox) - always show directly */}
      {mainItems.map((link) => (
        <Button
          key={link.path}
          component={Link}
          to={link.path}
          variant={location.pathname === link.path ? 'outlined' : 'text'}
          startIcon={getIconComponent(link.label)}
          sx={(theme) => {
            const active = location.pathname === link.path;
            return {
              textTransform: 'none',
              textAlign: 'center',
              fontSize: '0.875rem',
              px: { xs: 0.75, sm: 1, xl: 1.5 },
              py: 0.5,
              minWidth: 'auto',
              whiteSpace: 'nowrap',
              color: active ? '#fff' : 'rgba(238,242,232,0.82)',
              borderColor: alpha(theme.palette.primary.main, 0.75),
              ...(active && { backgroundColor: alpha(theme.palette.primary.main, 0.16) }),
              '&:hover': {
                color: '#fff',
                backgroundColor: 'rgba(238,242,232,0.08)',
              },
            };
          }}
        >
          {link.label === 'Inbox' && unreadMessagesCount > 0 ? (
            <Badge badgeContent={unreadMessagesCount} color="error">
              {link.label}
            </Badge>
          ) : (
            link.label
          )}
        </Button>
      ))}

      {/* Dynamic dropdowns (Jobs, Leads, Financials, Direct, Settings - adaptively split) */}
      {visibleDropdowns.map((dropdown) => (
        <React.Fragment key={dropdown.key}>
          <Tooltip title={dropdown.tooltip || ''} arrow>
            <Button
              aria-label={dropdown.tooltip || (dropdown.items.length === 1 ? dropdown.items[0].label : dropdown.label)}
              {...(dropdown.items.length === 1
                ? { component: Link, to: dropdown.items[0].path }
                : { onClick: (e: React.MouseEvent<HTMLElement>) => handleMenuOpen(e, dropdown.key) })}
              variant={dropdown.items.some(item => location.pathname === item.path) ? 'outlined' : 'text'}
              startIcon={dropdown.items.length === 1 ? getIconComponent(dropdown.items[0].label) : dropdown.icon}
              sx={(theme) => {
                const active = dropdown.items.some(item => location.pathname === item.path);
                return {
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  px: { xs: 0.75, sm: 1, xl: 1.5 },
                  py: 0.5,
                  minWidth: 'auto',
                  whiteSpace: 'nowrap',
                  color: active ? '#fff' : 'rgba(238,242,232,0.82)',
                  borderColor: alpha(theme.palette.primary.main, 0.75),
                  ...(active && { backgroundColor: alpha(theme.palette.primary.main, 0.16) }),
                  '&:hover': {
                    color: '#fff',
                    backgroundColor: 'rgba(238,242,232,0.08)',
                  },
                };
              }}
            >
              {dropdown.items.length === 1 ? dropdown.items[0].label : dropdown.label}
            </Button>
          </Tooltip>
          {dropdown.items.length > 1 && (
            <Menu
              anchorEl={anchorEl}
              open={openGroup === dropdown.key}
              onClose={handleMenuClose}
              PaperProps={{
                sx: { mt: 1, minWidth: 240, maxHeight: 400, overflowY: 'auto' }
              }}
            >
              {dropdown.items.map((item) => (
                <MenuItem
                  key={item.path}
                  component={Link}
                  to={item.path}
                  onClick={handleMenuClose}
                  selected={location.pathname === item.path}
                  sx={{ pl: 2 }}
                >
                  <ListItemIcon>
                    {itemBadge(item.label) > 0
                      ? <Badge badgeContent={itemBadge(item.label)} color="warning" max={99}>{getIconComponent(item.label)}</Badge>
                      : getIconComponent(item.label)}
                  </ListItemIcon>
                  <ListItemText>{item.label}</ListItemText>
                </MenuItem>
              ))}
            </Menu>
          )}
        </React.Fragment>
      ))}

      {/* Adaptive "More" dropdown for constrained viewports */}
      {moreDropdowns.length > 0 && (
        <>
          <Tooltip title="More navigation options" arrow>
            <Button
              id="more-nav-button"
              aria-controls={openGroup === 'more' ? 'more-nav-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={openGroup === 'more' ? 'true' : undefined}
              onClick={(e) => handleMenuOpen(e, 'more')}
              variant={isMoreActive ? 'outlined' : 'text'}
              startIcon={<MoreHorizIcon />}
              sx={(theme) => ({
                textTransform: 'none',
                fontSize: '0.875rem',
                px: { xs: 0.75, sm: 1, xl: 1.5 },
                py: 0.5,
                minWidth: 'auto',
                whiteSpace: 'nowrap',
                color: isMoreActive ? '#fff' : 'rgba(238,242,232,0.82)',
                borderColor: alpha(theme.palette.primary.main, 0.75),
                ...(isMoreActive && { backgroundColor: alpha(theme.palette.primary.main, 0.16) }),
                '&:hover': {
                  color: '#fff',
                  backgroundColor: 'rgba(238,242,232,0.08)',
                },
              })}
            >
              More
            </Button>
          </Tooltip>
          <Menu
            id="more-nav-menu"
            anchorEl={anchorEl}
            open={openGroup === 'more'}
            onClose={handleMenuClose}
            MenuListProps={{
              'aria-labelledby': 'more-nav-button',
            }}
            PaperProps={{
              sx: { mt: 1, minWidth: 260, maxHeight: 450, overflowY: 'auto' }
            }}
          >
            {moreDropdowns.flatMap((dropdown, idx) => [
              ...(idx > 0 ? [<Divider key={`divider-${dropdown.key}`} sx={{ my: 0.5 }} />] : []),
              <Box
                key={`header-${dropdown.key}`}
                sx={{
                  px: 2,
                  py: 0.75,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  backgroundColor: dropdown.color === 'secondary'
                    ? 'rgba(156, 39, 176, 0.06)'
                    : 'rgba(0, 0, 0, 0.02)',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    color: dropdown.color === 'secondary' ? 'secondary.main' : 'text.secondary',
                    fontSize: '1rem',
                  }}
                >
                  {dropdown.icon}
                </Box>
                <Typography variant="overline" color="text.secondary" fontWeight={700} sx={{ letterSpacing: 0.5 }}>
                  {dropdown.label}
                </Typography>
              </Box>,
              ...dropdown.items.map((item) => (
                <MenuItem
                  key={item.path}
                  component={Link}
                  to={item.path}
                  onClick={handleMenuClose}
                  selected={location.pathname === item.path}
                  sx={{ pl: 3, py: 0.75 }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {itemBadge(item.label) > 0 ? (
                      <Badge badgeContent={itemBadge(item.label)} color="warning" max={99}>
                        {getIconComponent(item.label)}
                      </Badge>
                    ) : (
                      getIconComponent(item.label)
                    )}
                  </ListItemIcon>
                  <ListItemText primaryTypographyProps={{ variant: 'body2' }}>{item.label}</ListItemText>
                </MenuItem>
              )),
            ])}
          </Menu>
        </>
      )}

      {/* Admin dropdown - platform admins only, with categorized sub-groups */}
      {adminItems.length > 0 && (
        <>
          <AdminCommandPalette permissions={userPermissions} />
          <Tooltip title="Platform Administration" arrow>
            <Button
              onClick={(e) => handleMenuOpen(e, 'admin')}
              variant={adminItems.some(item => location.pathname === item.path) ? 'outlined' : 'text'}
              startIcon={<AdminPanelSettingsIcon />}
              sx={(theme) => ({
                textTransform: 'none',
                fontSize: '0.875rem',
                px: { xs: 0.75, sm: 1, xl: 1.5 },
                py: 0.5,
                minWidth: 'auto',
                whiteSpace: 'nowrap',
                color: theme.palette.warning.light,
                borderColor: alpha(theme.palette.warning.main, 0.6),
                backgroundColor: alpha(theme.palette.warning.main, 0.16),
                '&:hover': {
                  color: '#fff',
                  backgroundColor: alpha(theme.palette.warning.main, 0.28),
                }
              })}
            >
              Admin
            </Button>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={openGroup === 'admin'}
            onClose={handleMenuClose}
            MenuListProps={{ autoFocusItem: false }}
            PaperProps={{
              sx: {
                mt: 1,
                minWidth: 280,
                maxHeight: '70vh',
                overflowY: 'auto'
              }
            }}
          >
            <Box sx={(theme) => ({
              px: 2,
              py: 1,
              backgroundColor: alpha(theme.palette.warning.main, 0.08)
            })}>
              <Typography variant="overline" color="warning.dark" fontWeight="bold">
                Administration
              </Typography>
            </Box>
            <Divider />
            {/* Type to find any admin page; while searching, matches replace the groups */}
            <AdminNavSearch
              permissions={userPermissions}
              query={adminQuery}
              onQueryChange={setAdminQuery}
              onSelect={(match) => {
                handleMenuClose();
                navigate(match.path);
              }}
              autoFocus
            />
            <Divider />
            {/* Render admin items grouped by category */}
            {!/[a-z0-9]/i.test(adminQuery) && (() => {
              const adminNav = getAdminNavigationByCategory(user?.permissions || []);
              const categoryOrder = ['users', 'content', 'logistics', 'compliance', 'finance', 'system'];
              
              // Filter standard categories that have items, and append any other categories returned
              const categoriesToRender = [
                ...categoryOrder.filter(cat => adminNav[cat] && adminNav[cat].length > 0),
                ...Object.keys(adminNav).filter(cat => !categoryOrder.includes(cat) && adminNav[cat].length > 0)
              ];

              return categoriesToRender.map((category) => {
                const items = adminNav[category];
                const isExpanded = expandedCategories[category] ?? false;
                return (
                  <Box key={category}>
                    <ListItemButton
                      component="li"
                      role="menuitem"
                      aria-expanded={isExpanded}
                      aria-controls={`admin-category-collapse-${category}`}
                      onClick={() => {
                        setExpandedCategories(prev => ({
                          ...prev,
                          [category]: !prev[category]
                        }));
                      }}
                      sx={(theme) => ({
                        px: 2,
                        py: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: isExpanded
                          ? alpha(theme.palette.warning.main, 0.08)
                          : 'transparent',
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.warning.main, 0.12),
                        }
                      })}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <ListItemIcon sx={{ minWidth: 'auto', color: theme.palette.warning.main }}>
                          {getCategoryIcon(category)}
                        </ListItemIcon>
                        <ListItemText
                          primary={adminCategoryLabels[category] || category}
                          primaryTypographyProps={{
                            variant: 'subtitle2',
                            fontWeight: 'bold',
                            color: isExpanded ? 'warning.dark' : 'text.primary'
                          }}
                        />
                      </Box>
                      <ExpandMoreIcon
                        sx={{
                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s',
                          color: 'text.secondary'
                        }}
                      />
                    </ListItemButton>
                    <Collapse id={`admin-category-collapse-${category}`} in={isExpanded} timeout="auto" unmountOnExit>
                      <Box sx={{ py: 0.5, backgroundColor: 'action.hover' }}>
                        {items.map((item) => (
                          <MenuItem
                            key={item.path}
                            component={Link}
                            to={item.path}
                            onClick={handleMenuClose}
                            selected={location.pathname === item.path}
                            sx={{ py: 1, pl: 5 }}
                          >
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              {getIconComponent(item.label)}
                            </ListItemIcon>
                            <ListItemText primaryTypographyProps={{ variant: 'body2' }}>{item.label}</ListItemText>
                          </MenuItem>
                        ))}
                      </Box>
                    </Collapse>
                  </Box>
                );
              });
            })()}
          </Menu>
        </>
      )}
    </Box>
  );
};

export default RoleBasedNavigation;
